import { withoutTrailingSlash } from "./realm";

/**
 * Anything that may carry the registration flag: the `/auth/me` profile, or a
 * raw claim set. The Keycloak custom mapper writes `registered` to the access
 * token only, so in the browser it is reached through `/auth/me`, never through
 * the OIDC id-token profile.
 */
type RegistrationSource = { registered?: unknown } | undefined;

/**
 * Where to send an unregistered lantbrukare so they can register.
 *
 * Agronod konto (fence-frontend) shows its registration flow on `/` and, once the
 * user is registered, sends them to `redirectUrl` exactly as given. The path has
 * to be exactly `/`: fence bounces unregistered users from any other path to `/`
 * and the query is lost on the way.
 *
 * @param kontoBaseUrl - `REACT_APP_AGRONOD_KONTO_URL`, with or without a trailing slash
 * @param returnUrl - absolute URL to come back to, normally `window.location.href`
 * @returns the registration URL carrying the encoded return URL
 */
export const buildRegistrationUrl = (kontoBaseUrl: string, returnUrl: string): string =>
  `${withoutTrailingSlash(kontoBaseUrl)}/?redirectUrl=${encodeURIComponent(returnUrl)}`;

/**
 * Whether the source carries the registration flag at all.
 *
 * A lantbrukare without it means the `agronod_profile` scope is missing from
 * the client; callers use this to make that misconfiguration visible.
 */
export const hasRegisteredClaim = (source: RegistrationSource): boolean =>
  source?.registered !== undefined;

/**
 * Whether the lantbrukare has registered.
 *
 * The flag is a boolean, but claims from the same mapper have been seen
 * arriving as JSON strings (see `parseKonton` in AgronodKontoContext), so the
 * string form is accepted too. Anything else, including a missing flag, counts
 * as not registered.
 *
 * @param source - the `/auth/me` profile or claim set, if any
 */
export const isRegistered = (source: RegistrationSource): boolean => {
  const value = source?.registered;
  return value === true || value === "true";
};
